import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import toast from 'react-hot-toast';
import PageContainer from '../PageContainer';
import { PortalTableCell, PortalTableHead, PortalTableRow } from '../../styles/portalListTable';
import PortalPaginationControls from '../layout/PortalPaginationControls';
import { useAdminsQuery } from '../../api/use-admins-query';
import { useGrantAdminMutation } from '../../api/use-grant-admin-mutation';
import { useRevokeAdminMutation } from '../../api/use-revoke-admin-mutation';
import { useAuth } from '../../contexts/AuthContext';
import { LIST_ROWS_PER_PAGE, VISIBLE_PAGE_COUNT } from '../../constants';
import { getUserInitial } from '../../utils/userDisplay';

const AdminManagement = () => {
  const { adminProfile } = useAuth();
  const [page, setPage] = useState(1);
  const grantAdminMutation = useGrantAdminMutation();
  const revokeAdminMutation = useRevokeAdminMutation();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [adminToRevoke, setAdminToRevoke] = useState(null);
  const [form, setForm] = useState({ email: '', password: '', fullName: '' });

  const limit = LIST_ROWS_PER_PAGE;
  const queryParams = useMemo(() => ({ limit, page }), [limit, page]);

  const { data, isLoading, isFetching, error } = useAdminsQuery(queryParams);

  const admins = data?.admins ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const currentPage = Math.min(page, totalPages);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const handleOpenAddDialog = () => {
    setForm({ email: '', password: '', fullName: '' });
    setAddDialogOpen(true);
  };

  const handleCloseAddDialog = () => {
    if (grantAdminMutation.isPending) return;
    setAddDialogOpen(false);
  };

  const handleGrantAdmin = async (e) => {
    e.preventDefault();
    const result = await grantAdminMutation.mutateAsync({
      email: form.email.trim(),
      password: form.password,
      fullName: form.fullName.trim(),
    });
    toast.success(result.message || 'Admin access granted');
    setAddDialogOpen(false);
    setPage(1);
  };

  const handleOpenRevokeDialog = (admin) => {
    if (admin.id === adminProfile?.id) return;
    setAdminToRevoke(admin);
  };

  const handleCloseRevokeDialog = () => {
    if (revokeAdminMutation.isPending) return;
    setAdminToRevoke(null);
  };

  const handleConfirmRevoke = async () => {
    if (!adminToRevoke?.id) return;
    await revokeAdminMutation.mutateAsync(adminToRevoke.id);
    setAdminToRevoke(null);
  };

  return (
    <PageContainer title="Admin Management">
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AdminPanelSettingsIcon />}
          onClick={handleOpenAddDialog}
        >
          Add admin
        </Button>
      </Box>

      {isLoading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress color="primary" />
        </Box>
      )}

      {error && (
        <Typography color="error" variant="body2">
          {error.message}
        </Typography>
      )}

      {!isLoading && !error && (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              bgcolor: 'background.paper',
              overflow: 'hidden',
            }}
          >
            <TableContainer
              sx={{
                maxHeight: 'calc(100vh - 320px)',
                minHeight: 0,
                overflow: 'auto',
              }}
            >
              <Table stickyHeader>
                <PortalTableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </PortalTableHead>
                <TableBody>
                  {admins.length === 0 ? (
                    <PortalTableRow>
                      <PortalTableCell colSpan={3}>
                        <Typography variant="body2" color="text.secondary">
                          No admin users found.
                        </Typography>
                      </PortalTableCell>
                    </PortalTableRow>
                  ) : (
                    admins.map((admin) => (
                      <PortalTableRow key={admin.id || admin.email}>
                        <PortalTableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar
                              src={admin.avatarUrl || undefined}
                              alt={admin.fullName || admin.email}
                            >
                              {getUserInitial(admin.fullName, admin.email)}
                            </Avatar>
                            <Typography variant="body2">{admin.fullName || '—'}</Typography>
                          </Box>
                        </PortalTableCell>
                        <PortalTableCell>{admin.email}</PortalTableCell>
                        <PortalTableCell align="right">
                          <IconButton
                            aria-label="Revoke admin access"
                            onClick={() => handleOpenRevokeDialog(admin)}
                            disabled={
                              revokeAdminMutation.isPending || admin.id === adminProfile?.id
                            }
                            color="error"
                          >
                            <PersonRemoveIcon />
                          </IconButton>
                        </PortalTableCell>
                      </PortalTableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {total > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 1.5,
                  borderTop: '1px solid',
                  borderTopColor: 'divider',
                  bgcolor: 'background.paper',
                  flexShrink: 0,
                }}
              >
                <PortalPaginationControls
                  currentPage={currentPage}
                  totalPages={totalPages}
                  visibleCount={VISIBLE_PAGE_COUNT}
                  isLoading={isFetching}
                  onPageChange={setPage}
                />
              </Box>
            )}
          </Box>
        </>
      )}

      <Dialog open={addDialogOpen} onClose={handleCloseAddDialog} fullWidth maxWidth="sm">
        <form onSubmit={handleGrantAdmin}>
          <DialogTitle>Add admin user</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Creates a Firebase account with the email and password below, then grants admin panel
              access for that user.
            </Typography>
            <TextField
              autoFocus
              margin="dense"
              label="Email"
              type="email"
              fullWidth
              required
              disabled={grantAdminMutation.isPending}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <TextField
              margin="dense"
              label="Password"
              type="password"
              fullWidth
              required
              disabled={grantAdminMutation.isPending}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              inputProps={{ minLength: 6 }}
              helperText="Minimum 6 characters — used for admin dashboard sign-in"
            />
            <TextField
              margin="dense"
              label="Full name"
              fullWidth
              required
              disabled={grantAdminMutation.isPending}
              value={form.fullName}
              onChange={(e) => setForm({ ...form, fullName: e.target.value })}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAddDialog}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={grantAdminMutation.isPending}
            >
              {grantAdminMutation.isPending ? 'Adding…' : 'Add admin'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={Boolean(adminToRevoke)}
        onClose={handleCloseRevokeDialog}
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 2,
            textAlign: 'center',
            maxWidth: 400,
            width: '100%',
          },
        }}
      >
        <DialogContent sx={{ pb: 1, pt: 1 }}>
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              bgcolor: '#fce8e6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <PersonRemoveIcon sx={{ fontSize: 32, color: '#f44336' }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Remove admin access?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            Are you sure you want to remove admin access for{' '}
            <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {adminToRevoke?.fullName || adminToRevoke?.email || 'this user'}
            </Box>
            ?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 1, pt: 2 }}>
          <Button
            variant="outlined"
            onClick={handleCloseRevokeDialog}
            disabled={revokeAdminMutation.isPending}
            sx={{ minWidth: 100, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmRevoke}
            disabled={revokeAdminMutation.isPending}
            sx={{ minWidth: 100, textTransform: 'none' }}
          >
            {revokeAdminMutation.isPending ? 'Removing…' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default AdminManagement;
